import {Box, Tab, Tabs, useMediaQuery} from '@mui/material';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {t} from 'i18next';
import {RootState} from '../../../../../../redux/store';
import {JobDetailData} from '../../../../../../redux/slice/aiReviewSliceV2';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';
import ProtocolCaseContext from '../../../common/casecontextv2/ProtocolCaseContext';
import IssueCodesTab from '../../../common/issue-codes-tab/IssueCodesTab';
import RelevanceCriteria from '../../../common/relevance-criteria/RelevanceCriteria';
import JobDetailStep from '../job-detail-step/JobDetailStep';
import SourceSelectionStep from '../source-selection-step/SourceSelectionStep';
import ProtocolSelection, {
  type ProtocolSelectionChangeType,
} from '../protocol-selection/ProtocolSelection';
import DraftProtocolChangedBanner from '../../../common/draft-protocol-changed-banner/DraftProtocolChangedBanner';
import './NewJobDetailTabs.scss';

export interface NewJobDetailTabsProps {
  jobDetail: JobDetailData;
  onJobDetailChange: (data: JobDetailData) => void;
  isCreatingGroup: boolean;
  setIsCreatingGroup: React.Dispatch<React.SetStateAction<boolean>>;
  isDraftProtocolChangedAfterJobModified?: boolean;
}

interface TabData {
  id: string;
  label: string;
  content: React.ReactNode;
}

const NewJobDetailTabs = ({
  jobDetail,
  onJobDetailChange,
  isCreatingGroup,
  setIsCreatingGroup,
  isDraftProtocolChangedAfterJobModified = false,
}: NewJobDetailTabsProps) => {
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);
  const [activeTab, setActiveTab] = useState(0);
  const [isProtocolChangeBannerDismissed, setIsProtocolChangeBannerDismissed] =
    useState(false);
  const hasProtocolAssociationChangedRef = useRef(false);

  const handleProtocolSelectionChange = useCallback(
    (changeType: ProtocolSelectionChangeType) => {
      if (changeType === 'applied' || changeType === 'cleared') {
        hasProtocolAssociationChangedRef.current = true;
        setIsProtocolChangeBannerDismissed(true);
        return;
      }

      if (changeType === 'reset') {
        if (!hasProtocolAssociationChangedRef.current) {
          setIsProtocolChangeBannerDismissed(false);
        }
        return;
      }

      setIsProtocolChangeBannerDismissed(true);
    },
    []
  );

  const tabData: TabData[] = useMemo(() => {
    const isIssueCoding = jobType === AI_REVIEW_JOB_TYPE.issueCoding;

    return [
      {
        id: 'job-details',
        label: t('ai.review.newJobDetail.jobDetails'),
        content: (
          <JobDetailStep
            data={jobDetail}
            setData={onJobDetailChange}
            isCreatingGroup={isCreatingGroup}
            setIsCreatingGroup={setIsCreatingGroup}
          />
        ),
      },
      {
        id: 'case-context',
        label: t('ai.review.newJobDetail.caseContext'),
        content: <ProtocolCaseContext mode="edit" />,
      },
      {
        id: isIssueCoding ? 'issue-codes' : 'relevance-criteria',
        label: isIssueCoding
          ? t('ai.review.newJobDetail.issueCodes')
          : t('ai.review.newJobDetail.relevanceCriteria'),
        content: isIssueCoding ? (
          <IssueCodesTab mode="edit" />
        ) : (
          <RelevanceCriteria mode="edit" />
        ),
      },
      {
        id: 'source-selection',
        label: t('ai.review.newJobDetail.sourceSelection'),
        content: <SourceSelectionStep />,
      },
    ];
  }, [
    jobType,
    jobDetail,
    onJobDetailChange,
    isCreatingGroup,
    setIsCreatingGroup,
  ]);

  useEffect(() => {
    setActiveTab(0);
  }, [jobType]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `new-job-detail-tab-${index}`,
    'aria-controls': `new-job-detail-tabpanel-${index}`,
  });

  const activeTabId = tabData[activeTab]?.id;
  const showProtocolSelection =
    activeTabId === 'case-context' ||
    activeTabId === 'issue-codes' ||
    activeTabId === 'relevance-criteria';
  const isNarrowViewport = useMediaQuery('(max-width:1200px)');
  const stackProtocolSelection = showProtocolSelection && isNarrowViewport;

  return (
    <Box
      className="new-job-detail-tabs-container"
      data-testid="new-job-detail-tabs"
    >
      {stackProtocolSelection && (
        <ProtocolSelection
          onProtocolSelectionChange={handleProtocolSelectionChange}
        />
      )}
      <Box className="new-job-detail-tabs-body">
      <Box className="new-job-detail-header-tabs">
        <Tabs
          value={activeTab}
          onChange={handleChange}
          className="new-job-detail-tabs"
          aria-label={t('ai.review.newJobDetail.jobSetupTabs')}
        >
          {tabData.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              className="new-job-detail-tab"
              data-testid={`new-job-detail-tab-${tab.id}`}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
        {showProtocolSelection && !stackProtocolSelection && (
          <ProtocolSelection
            onProtocolSelectionChange={handleProtocolSelectionChange}
          />
        )}
      </Box>
      {showProtocolSelection && (
        <DraftProtocolChangedBanner
          context="savedDraft"
          isVisible={
            Boolean(isDraftProtocolChangedAfterJobModified) &&
            !isProtocolChangeBannerDismissed
          }
        />
      )}
      <Box
        className="new-job-detail-tab-content"
        role="tabpanel"
        id={`new-job-detail-tabpanel-${activeTab}`}
        aria-labelledby={`new-job-detail-tab-${activeTab}`}
        data-testid={`new-job-detail-tabpanel-${tabData[activeTab]?.id}`}
      >
        {tabData[activeTab]?.content}
      </Box>
      </Box>
    </Box>
  );
};

export default NewJobDetailTabs;
